// @flow

import React, { useEffect, useState } from 'react';
import { Panel, PanelBar, PanelButtonBar, PanelHeader } from '../../../element/Panel';
import { PasswordInput, } from '../../../element/Input';
import { Notice } from '../../../element/Notice';
import { RC_API_REQUEST } from '../../../../state/resource/type';

export type PasswordEditorMessageType = {
    +messages?: Array<{ +id?: string, +text: string, +type: 'error' | 'info' | 'success' }>,
    +status: MessageStatus,
    +error?: ?string,
    +clear?: () => void,
};

type UserPasswordEditorProps = {
    +onSubmit: (
        currentPassword: string,
        newPassword: string,
        confirmPassword: string
    ) => Promise<boolean>,
    +onCancel: () => void,
    +onMount: () => void,
    +messages: PasswordEditorMessageType,
}

export const UserPasswordEditor = ({ onSubmit, onCancel, onMount, messages, }: UserPasswordEditorProps): React$Node => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const apiInProgress = messages?.status === RC_API_REQUEST;

    useEffect(() => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        if (typeof onMount === 'function') {
            onMount();
        }
    }, [onMount]);

    const handleSubmit = async () => {
        const success = await onSubmit(
            currentPassword,
            newPassword,
            confirmPassword
        );

        if (success) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            onCancel();
        }
    };

    const canSubmit = currentPassword &&
                      newPassword &&
                      newPassword.length >= 8 &&
                      confirmPassword;

    return (
        <Panel>
            <PanelHeader
                title='Change Password'
                description='Choose a new password to use to sign in as your Brightbox user profile'
            />
            <PanelBar>
                {messages?.error
                    ? <Notice type='error'>{messages.error}</Notice>
                    : null
                }
                <div className='max-w-md'>
                    <PasswordInput
                        label='New Password'
                        id='new_password'
                        autoComplete='new-password'
                        value={newPassword}
                        onChange={setNewPassword}
                        helperText='At least 8 characters'
                        disabled={apiInProgress}
                    />
                </div>
                <div className='max-w-md'>
                    <PasswordInput
                        label='Confirm Password'
                        id='confirm_password'
                        autoComplete='new-password'
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        disabled={apiInProgress}
                    />
                </div>
                <hr />
                <div className='max-w-md'>
                    <PasswordInput
                        label='Current Password'
                        id='current_password'
                        autoComplete='current-password'
                        value={currentPassword}
                        onChange={setCurrentPassword}
                        disabled={apiInProgress}
                    />
                </div>
            </PanelBar>
            <PanelButtonBar
                cacheStatus={messages?.status}
                primaryButton={{
                    onClick: () => { handleSubmit(); },
                    children: 'Change Password',
                    disabled: !canSubmit || apiInProgress,
                }}
                cancel={onCancel}
            />
        </Panel>
    );
};